import { useState, useEffect } from "react";
import {
  RefreshCw, Search, Send, CheckCircle, X,
  Activity, BarChart2, Dumbbell, Clock, BellRing
} from "lucide-react";
import { API } from "./adminConstants";

export default function VoortgangTab({ token }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("recent");
  const [sendingId, setSendingId] = useState(null);
  const [sentIds, setSentIds] = useState(new Set());
  const [bulkSending, setBulkSending] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const headers = { Authorization: `Bearer ${token}` };

  const load = () => {
    setLoading(true);
    fetch(`${API}/api/admin/voortgang`, { headers })
      .then(r => r.json())
      .then(d => setData(Array.isArray(d) ? d : []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [token]);

  const sendReminder = async (userId) => {
    setSendingId(userId);
    try {
      const res = await fetch(`${API}/api/admin/send-reminder/${userId}`, { method: "POST", headers });
      if (!res.ok) throw new Error();
      setSentIds(prev => new Set([...prev, userId]));
    } catch { alert("Versturen mislukt. Controleer de Resend configuratie."); }
    setSendingId(null);
  };

  const sendBulk = async () => {
    if (!window.confirm("Wil je een motiverende herinnering sturen naar ALLE klanten die >14 dagen niet hebben getraind?")) return;
    setBulkSending(true);
    setBulkResult(null);
    try {
      const res = await fetch(`${API}/api/admin/send-reminders-bulk`, { method: "POST", headers });
      const d = await res.json();
      setBulkResult(d);
    } catch { alert("Bulk versturen mislukt."); }
    setBulkSending(false);
  };

  const getStatus = (u) => {
    if (!u.last_trained) return { label: "Nieuw", cls: "text-zinc-400 bg-zinc-500/10 border-zinc-500/20" };
    const days = (Date.now() - new Date(u.last_trained)) / 86400000;
    if (days <= 7)  return { label: "Actief",     cls: "text-emerald-400 bg-emerald-400/10 border-emerald-400/20" };
    if (days <= 21) return { label: "Regelmatig", cls: "text-[#C9A84C] bg-[#C9A84C]/10 border-[#C9A84C]/20" };
    return               { label: "Inactief",   cls: "text-red-400 bg-red-400/10 border-red-400/20" };
  };

  const fmtDate = (iso) => {
    if (!iso) return "—";
    return new Date(iso).toLocaleDateString("nl-NL", { day: "numeric", month: "short", year: "numeric" });
  };

  const fmtDaysAgo = (iso) => {
    if (!iso) return null;
    const days = Math.floor((Date.now() - new Date(iso)) / 86400000);
    if (days === 0) return "vandaag";
    if (days === 1) return "gisteren";
    return `${days}d geleden`;
  };

  let filtered = data.filter(u =>
    u.name.toLowerCase().includes(search.toLowerCase()) ||
    u.email.toLowerCase().includes(search.toLowerCase())
  );
  if (sortBy === "naam")    filtered = [...filtered].sort((a, b) => a.name.localeCompare(b.name));
  if (sortBy === "sessies") filtered = [...filtered].sort((a, b) => b.total_sessions - a.total_sessions);

  const actief     = data.filter(u => u.last_trained && (Date.now() - new Date(u.last_trained)) / 86400000 <= 7).length;
  const totalSess  = data.reduce((s, u) => s + u.total_sessions, 0);
  const metSchemas = data.filter(u => u.purchased_schemas?.length > 0).length;

  return (
    <div>
      <div className="grid grid-cols-3 gap-4 mb-6">
        {[
          { icon: <Activity size={16} />, label: "Actieve klanten (7d)", val: actief, accent: "text-emerald-400" },
          { icon: <BarChart2 size={16} />, label: "Totale sessies gelogd", val: totalSess, accent: "text-[#C9A84C]" },
          { icon: <Dumbbell size={16} />, label: "Met schema toegewezen", val: metSchemas, accent: "text-blue-400" },
        ].map(({ icon, label, val, accent }) => (
          <div key={label} className="bg-[#111] border border-white/5 rounded-xl p-4 flex items-center gap-4">
            <div className={`${accent} bg-white/5 rounded-lg p-2`}>{icon}</div>
            <div>
              <p className="text-white font-bold text-xl leading-none">{val}</p>
              <p className="text-zinc-500 text-xs mt-1">{label}</p>
            </div>
          </div>
        ))}
      </div>

      {bulkResult && (
        <div className="mb-5 flex items-center gap-3 bg-emerald-400/10 border border-emerald-400/20 rounded-xl px-5 py-3">
          <CheckCircle size={16} className="text-emerald-400 shrink-0" />
          <p className="text-emerald-300 text-sm">
            <span className="font-bold">{bulkResult.sent}</span> herinneringen verstuurd
            {bulkResult.skipped > 0 && <span className="text-emerald-600"> · {bulkResult.skipped} overgeslagen (recent actief)</span>}
          </p>
          <button onClick={() => setBulkResult(null)} className="ml-auto text-emerald-600 hover:text-emerald-400"><X size={14} /></button>
        </div>
      )}

      <div className="flex items-center gap-3 mb-5">
        <div className="relative flex-1">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Zoek op naam of e-mail..."
            className="w-full bg-[#111] border border-white/8 rounded-lg pl-9 pr-4 py-2.5 text-sm text-zinc-300 placeholder-zinc-600 focus:outline-none focus:border-[#C9A84C]/40" />
        </div>
        <select value={sortBy} onChange={e => setSortBy(e.target.value)}
          className="bg-[#111] border border-white/8 rounded-lg px-3 py-2.5 text-sm text-zinc-400 focus:outline-none">
          <option value="recent">Sorteer: Meest recent</option>
          <option value="sessies">Sorteer: Meeste sessies</option>
          <option value="naam">Sorteer: Naam A–Z</option>
        </select>
        <button onClick={sendBulk} disabled={bulkSending} data-testid="send-bulk-reminder-btn"
          title="Stuur herinnering naar alle inactieve klanten (>14 dagen)"
          className="flex items-center gap-2 px-4 py-2.5 bg-[#C9A84C]/10 border border-[#C9A84C]/30 hover:border-[#C9A84C]/60 text-[#C9A84C] rounded-lg text-xs font-bold uppercase tracking-wide transition-colors disabled:opacity-40 shrink-0">
          {bulkSending ? <RefreshCw size={12} className="animate-spin" /> : <BellRing size={12} />}
          {bulkSending ? "Versturen..." : "Alle inactieven"}
        </button>
        <button onClick={load} className="p-2.5 text-zinc-600 hover:text-[#C9A84C] transition-colors bg-[#111] border border-white/8 rounded-lg">
          <RefreshCw size={14} />
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-zinc-600">
          <RefreshCw size={18} className="animate-spin mr-2" /> Laden…
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-zinc-600 text-sm">Geen klanten gevonden</div>
      ) : (
        <div className="space-y-4">
          {filtered.map(u => {
            const status = getStatus(u);
            const maxW = Math.max(...u.weekly_sessions, 1);
            const hasSchemas = u.purchased_schemas?.length > 0;
            const weekLabels = ["8w", "7w", "6w", "5w", "4w", "3w", "2w", "1w"];
            return (
              <div key={u.user_id} data-testid={`voortgang-card-${u.user_id}`}
                className="bg-[#111] border border-white/6 rounded-2xl p-5 hover:border-white/10 transition-colors">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-[#C9A84C]/10 border border-[#C9A84C]/20 flex items-center justify-center shrink-0">
                    <span className="text-[#C9A84C] font-bold text-sm">
                      {(u.name || u.email || "?").charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <span className="text-white font-semibold text-sm truncate">{u.name || "—"}</span>
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${status.cls}`}>{status.label}</span>
                      {u.last_trained && <span className="text-zinc-600 text-[10px]">{fmtDaysAgo(u.last_trained)}</span>}
                      {(status.label === "Inactief" || status.label === "Nieuw" || status.label === "Regelmatig") && (
                        <button onClick={() => sendReminder(u.user_id)}
                          disabled={sendingId === u.user_id || sentIds.has(u.user_id)}
                          data-testid={`send-reminder-btn-${u.user_id}`}
                          title="Stuur motiverende herinneringsmail"
                          className={`ml-auto flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wide transition-all
                            ${sentIds.has(u.user_id)
                              ? "text-emerald-400 bg-emerald-400/10 border border-emerald-400/20 cursor-default"
                              : "text-zinc-400 bg-white/4 border border-white/8 hover:border-[#C9A84C]/40 hover:text-[#C9A84C]"
                            } disabled:opacity-50`}>
                          {sentIds.has(u.user_id) ? <><CheckCircle size={10} /> Verstuurd</>
                            : sendingId === u.user_id ? <><RefreshCw size={10} className="animate-spin" /> Sturen…</>
                            : <><Send size={10} /> Herinnering</>}
                        </button>
                      )}
                    </div>
                    <p className="text-zinc-600 text-xs mb-3 truncate">{u.email}</p>
                    <div className="flex flex-wrap gap-6">
                      <div className="flex-1 min-w-[180px]">
                        <p className="text-zinc-600 text-[10px] font-bold uppercase tracking-widest mb-2">Schema's</p>
                        {!hasSchemas ? (
                          <p className="text-zinc-700 text-xs italic">Geen schema's toegewezen</p>
                        ) : (
                          <div className="flex flex-wrap gap-1.5">
                            {u.purchased_schemas.map(s => {
                              const cnt = u.schema_counts[s.product] || 0;
                              return (
                                <div key={s.product} className="flex items-center gap-1">
                                  <span className="text-[10px] text-zinc-400 bg-zinc-800 border border-white/5 rounded-md px-2 py-0.5 max-w-[160px] truncate">{s.label}</span>
                                  {cnt > 0 && <span className="text-[10px] font-bold text-emerald-400 bg-emerald-400/10 border border-emerald-400/20 rounded-full px-1.5 py-0.5">{cnt}×</span>}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                      <div className="shrink-0">
                        <p className="text-zinc-600 text-[10px] font-bold uppercase tracking-widest mb-2">Sessies per week</p>
                        <div className="flex items-end gap-1 h-10">
                          {u.weekly_sessions.map((cnt, i) => (
                            <div key={i} className="flex flex-col items-center gap-0.5" title={`${weekLabels[i]}: ${cnt} sessie${cnt !== 1 ? "s" : ""}`}>
                              <div className={`w-4 rounded-sm transition-all ${cnt > 0 ? "bg-[#C9A84C]" : "bg-white/5"}`}
                                style={{ height: `${Math.max((cnt / maxW) * 36, cnt > 0 ? 4 : 2)}px` }} />
                              <span className="text-[8px] text-zinc-700">{weekLabels[i]}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="shrink-0 flex flex-col gap-1 self-end">
                        <div className="flex items-center gap-1.5 text-xs">
                          <BarChart2 size={11} className="text-[#C9A84C]" />
                          <span className="text-white font-semibold">{u.total_sessions}</span>
                          <span className="text-zinc-600">sessies totaal</span>
                        </div>
                        <div className="flex items-center gap-1.5 text-xs">
                          <Clock size={11} className="text-zinc-600" />
                          <span className="text-zinc-500">{fmtDate(u.last_trained)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
